export type DashboardMetric = 'sales' | 'revenue' | 'checkins';

export const isSaleOrder = (order: any) => order?.status === 'completed' || order?.status === 'confirmed';

// Same payout math as the organizer order views: total minus platform fee, tax and Stripe fee.
// Legacy orders without a stored Stripe fee estimate it at 2.9% + $0.30, rounded up to the cent.
export function orderNet(order: any): number {
    const total = Number(order?.totalAmount) || 0;
    const platformFee = Number(order?.feeAmount) || 0;
    const storedStripeFee = Number(order?.stripeFeeAmount) || 0;
    if (storedStripeFee) return Math.max(0, total - platformFee - (Number(order?.taxAmount) || 0) - storedStripeFee);
    const ticketAmount = Math.max(0, total - platformFee);
    const stripeFee = ticketAmount > 0 ? Math.ceil((ticketAmount * 0.029 + 0.3) * 100 - 1e-9) / 100 : 0;
    return Math.max(0, ticketAmount - stripeFee);
}

/** Refunded and pending-refund orders, by count and order total. */
export function refundSummary(orders: any[]) {
    const refunded = (orders || []).filter((order) => order?.status === 'refunded' || order?.status === 'pending-refund');
    return { count: refunded.length, amount: refunded.reduce((total, order) => total + (Number(order.totalAmount) || 0), 0) };
}

const sum = (items: any[], pick: (item: any) => number) => items.reduce((total, item) => total + (pick(item) || 0), 0);
const soldAt = (order: any) => new Date(order.createdAt || order.orderDate).getTime();
const buyerName = (order: any) => order.customerName || order.guestName || order.attendeeName || order.name || 'N/A';
const saleOrders = (orders: any[]) => (orders || []).filter((order) => isSaleOrder(order) && Array.isArray(order.tickets));

// Net revenue, sold, check-ins and walk-ins (cash) for the order tickets that match, split by ticket value like the breakdown table.
function statsFor(orders: any[], match: (ticket: any) => boolean) {
    let sold = 0, checkIn = 0, walkIns = 0, revenue = 0;
    const value = (ticket: any) => Math.max(0, (ticket.price || 0) - (ticket.promocodeDiscount || 0) - (ticket.memberPricingDiscount || 0)) * (ticket.quantity || 1);
    saleOrders(orders).forEach((order) => {
        const mine = order.tickets.filter(match);
        if (!mine.length) return;
        mine.forEach((ticket: any) => {
            sold += ticket.quantity || 0;
            if (ticket.checkedInAt) checkIn += Number(ticket.quantity) || 0;
            if (order.paymentMethod === 'cash') walkIns += ticket.quantity || 0;
        });
        const orderValue = sum(order.tickets, value);
        if (orderValue > 0) revenue += orderNet(order) * (sum(mine, value) / orderValue);
    });
    return { sold, checkIn, walkIns, revenue };
}

export const computeTierStats = (orders: any[], tier: string) => statsFor(orders, (ticket) => ticket?.tier === tier);

// Everything an organizer needs on the sales dashboard, using the same sale rules as the rest of the overview.
export function computeDashboard(orders: any[], capacity: number, tiers: { name: string; color?: string; seatCount: number }[] = []) {
    const all = orders || [];
    const sale = saleOrders(all);
    const gross = sum(sale, (order) => Number(order.totalAmount));
    const net = sum(sale, orderNet);
    const sold = sum(sale, (order) => sum(order.tickets, (ticket) => ticket.quantity));
    const checkIn = sum(sale, (order) => sum(order.tickets.filter((ticket: any) => ticket.checkedInAt), (ticket) => Number(ticket.quantity)));
    const walkIns = sum(sale.filter((order) => order.paymentMethod === 'cash'), (order) => sum(order.tickets, (ticket) => ticket.quantity));
    const cash = sale.filter((order) => order.paymentMethod === 'cash');
    const card = sale.filter((order) => order.paymentMethod !== 'cash');
    return {
        gross,
        net,
        deductions: Math.max(0, gross - net),
        discounts: sum(sale, (order) => (Number(order.promoDiscountAmount) || 0) + (Number(order.memberPricingDiscountAmount) || 0)),
        sold,
        checkIn,
        walkIns,
        orderCount: sale.length,
        averageOrder: sale.length ? gross / sale.length : 0,
        ticketsPerOrder: sale.length ? sold / sale.length : 0,
        sellThrough: capacity > 0 ? Math.round((sold / capacity) * 100) : 0,
        checkInRate: sold > 0 ? Math.round((checkIn / sold) * 100) : 0,
        refunds: refundSummary(all),
        payments: {
            card: { count: card.length, amount: sum(card, (order) => Number(order.totalAmount)) },
            cash: { count: cash.length, amount: sum(cash, (order) => Number(order.totalAmount)) },
        },
        byTier: tiers.map((tier) => ({ name: tier.name, color: tier.color, capacity: tier.seatCount || 0, ...computeTierStats(all, tier.name) })),
        recent: [...sale]
            .filter((order) => !Number.isNaN(soldAt(order)))
            .sort((a, b) => soldAt(b) - soldAt(a))
            .slice(0, 5)
            .map((order) => ({ id: order._id || order.id, name: buyerName(order), at: soldAt(order), quantity: sum(order.tickets, (ticket) => Number(ticket.quantity)), amount: orderNet(order) })),
    };
}

// Timestamped values for the chart: tickets sold and net revenue by order time, check-ins by check-in time.
export function seriesEntries(orders: any[], metric: DashboardMetric): { time: number; value: number }[] {
    const entries: { time: number; value: number }[] = [];
    saleOrders(orders).forEach((order) => {
        if (metric === 'checkins') {
            order.tickets.filter((ticket: any) => ticket.checkedInAt).forEach((ticket: any) => entries.push({ time: new Date(ticket.checkedInAt).getTime(), value: Number(ticket.quantity) || 1 }));
        } else if (metric === 'sales') {
            entries.push({ time: soldAt(order), value: sum(order.tickets, (ticket) => Number(ticket.quantity)) });
        } else {
            entries.push({ time: soldAt(order), value: orderNet(order) });
        }
    });
    return entries.filter((entry) => !Number.isNaN(entry.time));
}
